import api from './api';

// Parties API
export const partiesAPI = {
  list: (params) => api.get('/parties', { params }),
  get: (id) => api.get(`/parties/${id}`),
  create: (data) => api.post('/parties', data),
  update: (id, data) => api.put(`/parties/${id}`, data),
  delete: (id) => api.delete(`/parties/${id}`),
  autocomplete: (q, type) => api.get('/parties/autocomplete', { params: { q, type } }),
  getLedger: (id) => api.get(`/parties/${id}/ledger`),
};

// Items API
export const itemsAPI = {
  list: (params) => api.get('/items', { params }),
  get: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  autocomplete: (q) => api.get('/items/autocomplete', { params: { q } }),
  lookupByBarcode: (barcode) => api.get('/items/lookup', { params: { barcode } }),
  getStock: (id) => api.get(`/items/${id}/stock`),
  adjustStock: (id, data) => api.post(`/items/${id}/adjust-stock`, data),
  bulkUpdatePrice: (data) => api.post('/items/bulk/price', data),
  bulkUpdateTax: (data) => api.post('/items/bulk/tax', data),
  bulkUpdateStatus: (data) => api.post('/items/bulk/status', data),
  // New endpoints for product detail
  getTransactions: (id, params) => api.get(`/items/${id}/transactions`, { params }),
  getStockMovements: (id, params) => api.get(`/items/${id}/stock-movements`, { params }),
  addImage: (id, data) => api.post(`/items/${id}/images`, data),
  removeImage: (id, imageUrl) => api.post(`/items/${id}/remove-image`, { image_url: imageUrl }),
  setPrimaryImage: (id, imageUrl) => api.patch(`/items/${id}/primary-image`, null, { params: { image_url: imageUrl } }),
  uploadImage: (id, file, isPrimary = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/items/${id}/upload-image?is_primary=${isPrimary}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Custom Fields API
export const customFieldsAPI = {
  list: (params) => api.get('/custom-fields', { params }),
  get: (id) => api.get(`/custom-fields/${id}`),
  create: (data) => api.post('/custom-fields', data),
  update: (id, data) => api.put(`/custom-fields/${id}`, data),
  delete: (id) => api.delete(`/custom-fields/${id}`),
  reorder: (data) => api.post('/custom-fields/reorder', data),
};

// Documents API (Generic)
export const documentsAPI = {
  list: (params) => api.get('/documents', { params }),
  get: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  getPrintData: (id) => api.get(`/documents/${id}/print-data`),
};

// Proforma Invoices API
export const proformaInvoicesAPI = {
  list: (params) => api.get('/proforma-invoices', { params }),
  get: (id) => api.get(`/proforma-invoices/${id}`),
  create: (data) => api.post('/proforma-invoices', data),
  update: (id, data) => api.put(`/proforma-invoices/${id}`, data),
  delete: (id) => api.delete(`/proforma-invoices/${id}`),
  updateStatus: (id, status, reason) =>
    api.patch(`/proforma-invoices/${id}/status`, null, { params: { status, reason } }),
  convertToInvoice: (id) => api.post(`/proforma-invoices/${id}/convert-to-invoice`),
  downloadPdf: (id) => api.get(`/proforma-invoices/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/proforma-invoices/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/proforma-invoices/${id}/send-whatsapp`),
};

// Quotations API (Convenience)
export const quotationsAPI = {
  list: (params) => api.get('/quotations', { params }),
  get: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  getTemplateSettings: (templateKey) => api.get(`/templates/${templateKey}`),
  convertToInvoice: (id) => api.post(`/quotations/${id}/convert-to-invoice`),
  convertToSalesOrder: (id) => api.post(`/quotations/${id}/convert-to-sales-order`),
  downloadPdf: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/quotations/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/quotations/${id}/send-whatsapp`),
};

// Sales Returns API
export const salesReturnsAPI = {
  list: (params) => api.get('/sales-returns', { params }),
  get: (id) => api.get(`/sales-returns/${id}`),
  create: (data) => api.post('/sales-returns', data),
  update: (id, data) => api.put(`/sales-returns/${id}`, data),
  delete: (id) => api.delete(`/sales-returns/${id}`),
  confirm: (id) => api.post(`/sales-returns/${id}/confirm`),
  cancel: (id) => api.post(`/sales-returns/${id}/cancel`),
  recordRefund: (id, payload) => api.post(`/sales-returns/${id}/record-refund`, payload),
  createCreditNote: (id) => api.post(`/sales-returns/${id}/create-credit-note`),
  downloadPdf: (id) => api.get(`/sales-returns/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/sales-returns/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/sales-returns/${id}/send-whatsapp`),
  getFromInvoice: (invoiceId) => api.get(`/sales-returns/from-invoice/${invoiceId}`),
  getReasons: () => api.get('/sales-returns/reasons'),
};

// Sales API
export const salesAPI = {
  listInvoices: (params) => api.get('/sales/invoices', { params }),
  getInvoice: (id) => api.get(`/sales/invoices/${id}`),
  createInvoice: (data) => api.post('/sales/invoices', data),
  updateInvoice: (id, data) => api.put(`/sales/invoices/${id}`, data),
  finalizeInvoice: (id) => api.patch(`/sales/invoices/${id}/finalize`),
  cancelInvoice: (id) => api.patch(`/sales/invoices/${id}/cancel`),
  listPaymentsIn: (params) => api.get('/sales/payments-in', { params }),
  createPaymentIn: (data) => api.post('/sales/payments-in', data),
  getTemplateSettings: (templateKey) => api.get(`/templates/${templateKey}`),
};

// Purchases API (legacy -- targets non-existent /purchases/invoices route)
export const purchasesAPI = {
  listInvoices: (params) => api.get('/purchases/invoices', { params }),
  getInvoice: (id) => api.get(`/purchases/invoices/${id}`),
  createInvoice: (data) => api.post('/purchases/invoices', data),
  updateInvoice: (id, data) => api.put(`/purchases/invoices/${id}`, data),
  finalizeInvoice: (id) => api.post(`/purchases/invoices/${id}/finalize`),
  cancelInvoice: (id) => api.post(`/purchases/invoices/${id}/cancel`),
  listPaymentsOut: (params) => api.get('/purchases/payments-out', { params }),
  createPaymentOut: (data) => api.post('/purchases/payments-out', data),
};

// Purchase Invoices API (new -- targets real /purchase-invoices route)
export const purchaseInvoicesAPI = {
  list: (params) => api.get('/purchase-invoices', { params }),
  get: (id) => api.get(`/purchase-invoices/${id}`),
  create: (data) => api.post('/purchase-invoices', data),
  update: (id, data) => api.put(`/purchase-invoices/${id}`, data),
  confirm: (id) => api.post(`/purchase-invoices/${id}/confirm`),
  cancel: (id, reason) => api.post(`/purchase-invoices/${id}/cancel`, {}, { params: { reason } }),
  delete: (id) => api.delete(`/purchase-invoices/${id}`),
  pdf: (id) => api.get(`/purchase-invoices/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/purchase-invoices/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/purchase-invoices/${id}/send-whatsapp`),
  stats: () => api.get('/purchase-invoices/stats/summary'),
  openInvoices: (supplierId) => api.get(`/purchase-invoices/open-invoices/${supplierId}`),
};

// Cash & Bank API
export const cashBankAPI = {
  getSummary: () => api.get('/cash-bank/summary'),
  listAccounts: (params) => api.get('/cash-bank/accounts', { params }),
  getAccount: (id) => api.get(`/cash-bank/accounts/${id}`),
  createAccount: (data) => api.post('/cash-bank/accounts', data),
  updateAccount: (id, data) => api.put(`/cash-bank/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/cash-bank/accounts/${id}`),
  listTransactions: (params) => api.get('/cash-bank/transactions', { params }),
  getAccountTransactions: (id, params) => api.get(`/cash-bank/accounts/${id}/transactions`, { params }),
  adjust: (data) => api.post('/cash-bank/adjust', data),
  transfer: (data) => api.post('/cash-bank/transfer', data),
  getLedger: (id, params) => api.get(`/cash-bank/accounts/${id}/ledger`, { params }),
  pettyCash: () => api.get('/cash-bank/petty-cash'),
};

// Expenses API
export const expensesAPI = {
  list: (params) => api.get('/expenses', { params }),
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getCategories: () => api.get('/expenses/categories/list'),
  stats: () => api.get('/expenses/stats/summary'),
  pdf: (id) => api.get(`/expenses/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/expenses/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/expenses/${id}/send-whatsapp`),
};

// Staff & Payroll API
export const staffAPI = {
  list: (params) => api.get('/staff', { params }),
  get: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  updateStatus: (id, data) => api.patch(`/staff/${id}/status`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  markAttendance: (staffId, data) => api.post(`/staff/${staffId}/attendance`, data),
  getAttendance: (staffId, month) => api.get(`/staff/${staffId}/attendance`, { params: { month } }),
  getPayrollSummary: (month) => api.get('/staff/payroll', { params: { month } }),
  createPayrollRun: (data) => api.post('/staff/payroll/runs', data),
  listPayrollRuns: () => api.get('/staff/payroll/runs'),
  getPayrollEntries: (runId) => api.get(`/staff/payroll/runs/${runId}/entries`),
  paySalary: (entryId, data) => api.post(`/staff/payroll/entries/${entryId}/pay`, data),
  payDirect: (data) => api.post('/staff/payroll/pay', data),
};

// Assignments API
export const assignmentsAPI = {
  getCities: () => api.get('/part5/assignments/cities'),
  getRetailers: () => api.get('/parties', { params: { type: 'customer' } }),
  assignCityToStaff: (data) => api.post('/part5/assignments/city-to-staff', data),
  assignRetailersToStaff: (data) => api.post('/part5/assignments/retailers-to-staff', data),
  removeAssignment: (staffId, retailerId) => api.delete(`/part5/assignments/staff/${staffId}/retailer/${retailerId}`),
};

// Field Staff (Users & Roles) API
export const fieldStaffAPI = {
  list: (params) => api.get('/settings/users/field-staff', { params }),
  create: (data) => api.post('/settings/users/field-staff', data),
  update: (id, data) => api.put(`/settings/users/field-staff/${id}`, data),
  updateStatus: (id, data) => api.patch(`/settings/users/field-staff/${id}/status`, data),
  resetPassword: (id) => api.post(`/settings/users/field-staff/${id}/reset-password`, { user_id: id }),
  bulkAction: (data) => api.post('/settings/users/field-staff/bulk-action', data),
  getRoleTemplates: () => api.get('/settings/users/role-templates'),
};

// POS API
export const posAPI = {
  // Invoices
  createInvoice: (data) => api.post('/pos/invoice', data),
  listInvoices: (params) => api.get('/pos/invoices', { params }),
  getInvoice: (id) => api.get(`/pos/invoices/${id}`),
  getReceipt: (id) => api.get(`/pos/invoices/${id}/receipt`),
  // Holds
  createHold: (data) => api.post('/pos/hold', data),
  listHolds: () => api.get('/pos/holds'),
  restoreHold: (id) => api.post(`/pos/holds/${id}/restore`),
  deleteHold: (id) => api.delete(`/pos/holds/${id}`),
  // Refunds
  createRefund: (data) => api.post('/pos/refund', data),
  listRefunds: (params) => api.get('/pos/refunds', { params }),
  // Settings
  getSettings: () => api.get('/pos/settings'),
  updateSettings: (data) => api.put('/pos/settings', data),
  // Items
  searchItems: (q) => api.get('/pos/items/search', { params: { q } }),
  quickCreateItem: (data) => api.post('/pos/items/quick-create', data),
  // Daily Reports
  getDailyReport: (params) => api.get('/pos/daily-report', { params }),
  closeDay: (params) => api.post('/pos/daily-report/close', null, { params }),
  listDailyReports: (params) => api.get('/pos/daily-reports', { params }),
  // Cash Drawer
  getDrawerStatus: () => api.get('/pos/cash-drawer/status'),
  openDrawer: (data) => api.post('/pos/cash-drawer/open', data),
  closeDrawer: () => api.post('/pos/cash-drawer/close'),
  addCash: (data) => api.post('/pos/cash-drawer/add', data),
  removeCash: (data) => api.post('/pos/cash-drawer/remove', data),
  getTransactions: () => api.get('/pos/cash-drawer/transactions'),
};

// Reports API
export const reportsAPI = {
  salesSummary: (params) => api.get('/reports/sales-summary', { params }),
  purchaseSummary: (params) => api.get('/reports/purchase-summary', { params }),
  partyOutstanding: () => api.get('/reports/party-outstanding'),
  itemSales: (params) => api.get('/reports/item-sales', { params }),
  lowStock: () => api.get('/reports/low-stock'),
  cashBankSummary: () => api.get('/reports/cash-bank-summary'),
  expenseByCategory: (params) => api.get('/reports/expense-by-category', { params }),
  // GST reports
  gstr1: (params) => api.get('/reports/gstr1', { params }),
  gstr1Export: (params) => api.post('/reports/gstr1/export', {}, { params, responseType: 'blob' }),
  gstr3b: (params) => api.get('/reports/gstr3b', { params }),
  gstSummary: (params) => api.get('/reports/gst-summary', { params }),
  salesRegister: (params) => api.get('/reports/sales-register', { params }),
  purchaseRegister: (params) => api.get('/reports/purchase-register', { params }),
  gstr2b: (params) => api.get('/reports/gstr2b', { params }),
  itcReconciliation: (params) => api.get('/reports/itc-reconciliation', { params }),
  itcEligibility: (params) => api.get('/reports/itc-eligibility', { params }),
  booksVsGst: (params) => api.get('/reports/books-vs-gst', { params }),
  // Export & history
  exportReport: (data) => api.post('/reports/export', data, { responseType: 'blob' }),
  exportHistory: () => api.get('/reports/export-history'),
  emailReport: (data) => api.post('/reports/email', data),
};

// Sales Orders API
export const salesOrdersAPI = {
  list: (params) => api.get('/sales-orders', { params }),
  get: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  confirm: (id) => api.patch(`/sales-orders/${id}/confirm`),
  cancel: (id) => api.patch(`/sales-orders/${id}/cancel`),
  delete: (id) => api.delete(`/sales-orders/${id}`),
  pdf: (id) => api.get(`/sales-orders/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, email) => api.post(`/sales-orders/${id}/send-email`, {}, { params: email ? { email } : {} }),
  sendWhatsApp: (id, phone) => api.post(`/sales-orders/${id}/send-whatsapp`, {}, { params: phone ? { phone } : {} }),
};

// Payments Received API
export const paymentsReceivedAPI = {
  list: (params) => api.get('/payments-received', { params }),
  get: (id) => api.get(`/payments-received/${id}`),
  create: (data) => api.post('/payments-received', data),
  update: (id, data) => api.put(`/payments-received/${id}`, data),
  void: (id, reason) => api.post(`/payments-received/${id}/void`, {}, { params: { reason } }),
  allocate: (id, allocations) => api.post(`/payments-received/${id}/allocate`, allocations),
  delete: (id) => api.delete(`/payments-received/${id}`),
  pdf: (id) => api.get(`/payments-received/${id}/receipt-pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/payments-received/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/payments-received/${id}/send-whatsapp`),
  customerHistory: (customerId) => api.get(`/payments-received/customer/${customerId}/history`),
  openInvoices: (customerId) => api.get(`/payments-received/open-invoices/${customerId}`),
};

// Payments Made API
export const paymentsMadeAPI = {
  list: (params) => api.get('/payments-made', { params }),
  get: (id) => api.get(`/payments-made/${id}`),
  create: (data) => api.post('/payments-made', data),
  update: (id, data) => api.put(`/payments-made/${id}`, data),
  void: (id, reason) => api.post(`/payments-made/${id}/void`, {}, { params: { reason } }),
  allocate: (id, allocations) => api.post(`/payments-made/${id}/allocate`, allocations),
  delete: (id) => api.delete(`/payments-made/${id}`),
  pdf: (id) => api.get(`/payments-made/${id}/voucher-pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/payments-made/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/payments-made/${id}/send-whatsapp`),
  supplierHistory: (supplierId) => api.get(`/payments-made/supplier/${supplierId}/history`),
};

// Purchase Receives API
export const purchaseReceivesAPI = {
  list: (params) => api.get('/purchase-receives', { params }),
  get: (id) => api.get(`/purchase-receives/${id}`),
  create: (data) => api.post('/purchase-receives', data),
  confirm: (id) => api.patch(`/purchase-receives/${id}/confirm`),
  delete: (id) => api.delete(`/purchase-receives/${id}`),
  pdf: (id) => api.get(`/purchase-receives/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/purchase-receives/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/purchase-receives/${id}/send-whatsapp`),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  confirm: (id) => api.patch(`/purchase-orders/${id}/confirm`),
  cancel: (id, reason) => api.patch(`/purchase-orders/${id}/cancel`, {}, { params: { reason } }),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  pdf: (id) => api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/purchase-orders/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/purchase-orders/${id}/send-whatsapp`),
};

// Debit Notes API
export const debitNotesAPI = {
  list: (params) => api.get('/debit-notes', { params }),
  get: (id) => api.get(`/debit-notes/${id}`),
  create: (data) => api.post('/debit-notes', data),
  update: (id, data) => api.put(`/debit-notes/${id}`, data),
  delete: (id) => api.delete(`/debit-notes/${id}`),
  issue: (id) => api.patch(`/debit-notes/${id}/issue`),
  cancel: (id, reason) => api.patch(`/debit-notes/${id}/cancel`, {}, { params: { reason } }),
  adjust: (id, params) => api.post(`/debit-notes/${id}/adjust`, {}, { params }),
  getReasons: () => api.get('/debit-notes/reasons'),
  pdf: (id) => api.get(`/debit-notes/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/debit-notes/${id}/send-email`),
  sendWhatsApp: (id) => api.post(`/debit-notes/${id}/send-whatsapp`),
};

// Staff Dashboard API
export const staffDashboardAPI = {
  getStats: () => api.get('/staff-dashboard/my-stats'),
  getRecentInvoices: (params) => api.get('/staff-dashboard/my-recent-invoices', { params }),
  getRecentCollections: (params) => api.get('/staff-dashboard/my-recent-collections', { params }),
  getPerformance: (params) => api.get('/staff-dashboard/my-performance', { params }),
};

// Staff Self-Service API
export const staffSelfAPI = {
  getAttendanceHistory: (params) => api.get('/staff/self/my-attendance/history', { params }),
  startShift: (data) => api.post('/part3/shifts/start', data),
  endShift: (data) => api.post('/part3/shifts/end', data),
  getTodayShift: () => api.get('/part3/shifts/my-today'),
  sendLocation: (data) => api.post('/part3/tracking/location', data),
  getMyVisits: (params) => api.get('/part3/visits/my', { params }),
  checkinVisit: (visitId, data) => api.post(`/part3/visits/${visitId}/checkin`, data),
  checkoutVisit: (visitId, data) => api.post(`/part3/visits/${visitId}/checkout`, data),
  getMyRetailers: () => api.get('/part3/retailers/my'),
  getMyCollections: (params) => api.get('/staff/self/my-collections', { params }),
  getMyPerformance: (params) => api.get('/staff/self/my-performance', { params }),
  getMyProfile: () => api.get('/staff/self/my-profile'),
  updateMyProfile: (data) => api.put('/staff/self/my-profile', data),
  changePassword: (data) => api.post('/field-staff/change-password', data),
  getMyRetailersForForm: (params) => api.get('/staff/self/my-retailers', { params }),
  getMyPlacements: (params) => api.get('/staff/self/my-placements', { params }),
  recordPlacement: (data) => api.post('/staff/self/record-placement', data),
};

// Master Password API (owner-only)
export const masterPasswordAPI = {
  getStatus: () => api.get('/settings/master-password/status'),
  set: (data) => api.post('/settings/master-password', data),
  change: (data) => api.put('/settings/master-password', data),
};

// Intelligence API
export const intelligenceAPI = {
  getOverview: (params) => api.get('/part5/intelligence/owner/overview', { params }),
  getStaffList: (params) => api.get('/part5/intelligence/staff-list', { params }),
  getRetailerRisks: (params) => api.get('/part5/intelligence/retailers/risk', { params }),
  getAlerts: (params) => api.get('/part5/intelligence/alerts', { params }),
  updateAlertStatus: (id, data) => api.post(`/part5/intelligence/alerts/${id}/status`, data),
  recompute: (params) => api.post('/part5/intelligence/recompute', null, { params }),
  getMyPerformance: () => api.get('/part5/intelligence/me/today'),
  getMyAlerts: () => api.get('/part5/intelligence/me/alerts'),
};
